import React, { useEffect, useState } from "react";
import { TextField } from "@mui/material";
import { useLazyQuery } from "@apollo/client";

import useUrlParams from "../../../../customHooks/useUrlParams";
import {
  AtsCompanyEdge,
  AtsCompanyOrderField,
  GetCompaniesResult,
  GetCompaniesVars,
} from "./queries/types";
import { GetCompanies } from "./queries/queries";
import { FETCH_MORE_DATA, ROWS_PER_PAGE } from "../../../../utils/constants";
import { OrderDirection } from "../../../../utils/commonQueryTypes";
import { getModifiedScrollHeight } from "../../../../utils/UtilFunctions";
import {
  FormAutocomplete,
  formClasses,
  labelClasses,
  LabeledAutocomplete,
} from "../../../../styles/AutocompleteStyles";
import useLoggedInUserDetails from "../../../../customHooks/useLoggedInUserDetails";

export interface ItemsAutocompleteProps {
  onChange: (newValue: AtsCompanyEdge) => void;
  value: number;
  onKeyDown:
    | ((
        event: React.KeyboardEvent<HTMLDivElement> & {
          defaultMuiPrevented?: boolean;
        }
      ) => void)
    | undefined;
  autoFocus?: boolean;
  label?: string;
  placeholder?: string;
}
const CompaniesAutocomplete = ({
  onChange,
  onKeyDown,
  value,
  autoFocus,
  label,
  placeholder,
}: ItemsAutocompleteProps) => {
  const { customer_id } = useUrlParams();
  const [searchData, setSearchData] = useState("");
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [companies, setCompanies] = useState<AtsCompanyEdge[]>([]);
  const [autoValue, setAutoValue] = useState<AtsCompanyEdge | null>(null);

  const { user_details } = useLoggedInUserDetails();

  const [GetCompany, { data, loading, fetchMore }] = useLazyQuery<
    GetCompaniesResult,
    GetCompaniesVars
  >(GetCompanies);

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetCompanies.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = companies.find(
            (company) => company.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setCompanies(updatedNewData);
      } else {
        setCompanies(newData);
      }
      setEndCursor(data.GetCompanies.pageInfo.endCursor);
    }
    // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    if (customer_id) {
      GetCompany({
        variables: {
          after: null,
          customerID: customer_id,
          empID: user_details.id,
          first: ROWS_PER_PAGE,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsCompanyOrderField.NAME,
          },

          name: searchData,
        },
      });
    }
  }, [customer_id, user_details.id, GetCompany, searchData]);

  useEffect(() => {
    if (value) {
      const foundValue = companies.find(
        ({ node }) => Number(node.id) === Number(value)
      );

      setAutoValue(foundValue ? foundValue : null);
    } else {
      setAutoValue(null);
    }
  }, [value, data, companies]);

  const handleScroll = (e: React.UIEvent<HTMLUListElement, UIEvent>) => {
    const target = e.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
      if (hasNextPage && !loading) {
        fetchMore({
          variables: {
            first: FETCH_MORE_DATA,
            after: endCursor,
          },
          updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prevResult;

            const newEdges = fetchMoreResult.GetCompanies.edges;
            const pageInfo = fetchMoreResult.GetCompanies.pageInfo;
            setEndCursor(pageInfo.endCursor);
            setHasNextPage(pageInfo.hasNextPage);

            const duplicateCheck = prevResult.GetCompanies.edges.filter(
              ({ node: { id } }) =>
                newEdges.findIndex(
                  ({ node: { id: newId } }) => newId === id
                ) !== -1
            );

            if (duplicateCheck.length > 0) return prevResult;

            return {
              GetCompanies: {
                edges: [...companies, ...newEdges],
                pageInfo,
                totalCount: data ? data.GetCompanies.totalCount! : 0,
              },
            };
          },
        });
      }
    }
  };

  let commonAutocompleteClassName = label
    ? labelClasses.inputRoot
    : formClasses.inputRoot;
  let commonTextFieldClassName = label
    ? labelClasses.formControlRoot
    : formClasses.formControlRoot;

  const Autocomplete = label ? LabeledAutocomplete : FormAutocomplete;

  return (
    <Autocomplete
      className={commonAutocompleteClassName}
      options={companies}
      onKeyDown={onKeyDown}
      value={autoValue}
      openOnFocus
      forcePopupIcon
      getOptionLabel={(option) => (option as AtsCompanyEdge).node.name}
      slotProps={{
        listbox: {
          onScroll: handleScroll,
        },
      }}
      onChange={(e, newValue) => {
        onChange(newValue as AtsCompanyEdge);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          autoFocus={autoFocus}
          onChange={(e) => setSearchData(e.target.value)}
          className={commonTextFieldClassName}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          placeholder={placeholder}
        />
      )}
    />
  );
};

export default CompaniesAutocomplete;
